import { useCallback, useState } from 'react'
import type { MemberListItem } from '@ai-meeting/shared'
import { ApiError } from '@/services/api'
import { createMeetingShares } from '@/services/meetings'
import { getServiceStrings } from '@/services/i18n'
import { useStore } from '@/store/useStore'

export type InviteMeetingMembersResult = {
  ok: boolean
  /** How many recipients were invited on success (0 when ok is false). */
  count: number
}

/**
 * Invites BrSE.ai members into an already-running meeting (BRAI-533 mid-call).
 *
 * Calls the same `createMeetingShares` path as create-time invite. When the
 * meeting was started solo (`sharedRoomId` null), the backend attaches
 * `sharedRoomId = meeting.id` before publishing invites; this hook mirrors
 * that onto the Zustand row so live STT / room UI stay in sync without a
 * refetch.
 */
export function useInviteMeetingMembers(meetingId: string | null | undefined) {
  const updateMeeting = useStore((s) => s.updateMeeting)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invite = useCallback(
    async (selected: MemberListItem[]): Promise<InviteMeetingMembersResult> => {
      if (!meetingId) {
        setError(getServiceStrings().failedShareMeetingService)
        return { ok: false, count: 0 }
      }
      const userIds = [...new Set(selected.map((member) => member.id))]
      if (userIds.length === 0) return { ok: false, count: 0 }

      setSubmitting(true)
      setError(null)
      try {
        const meeting = useStore.getState().meetings.find((m) => m.id === meetingId)
        const hadNoRoom = meeting != null && meeting.sharedRoomId == null

        const result = await createMeetingShares(meetingId, { userIds })
        const count = result.shared.length
        // Empty `shared` means every recipient was filtered (already shared /
        // blocked) — do not toast "Invite sent to N" with the selected size.
        if (count === 0) {
          setError(getServiceStrings().failedShareMeetingService)
          return { ok: false, count: 0 }
        }

        if (hadNoRoom) {
          updateMeeting(meetingId, { sharedRoomId: meetingId })
        }
        return { ok: true, count }
      } catch (err: unknown) {
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : getServiceStrings().failedShareMeetingService
        )
        return { ok: false, count: 0 }
      } finally {
        setSubmitting(false)
      }
    },
    [meetingId, updateMeeting]
  )

  const clearError = useCallback(() => setError(null), [])

  return { invite, submitting, error, clearError, setError }
}
